/**
 * AUTH59 Slice A — Journey activity spatial marker／view model（純函式）。
 * 供 Planning 適配與未來 Overview 共用；不依賴 Overview UI／offline／Maps surface。
 */
import { normalizeCoordinatePair } from '../explore/socialMapCoordinate';

/** 未排程活動的穩定 group 識別（非假 dayId）。 */
export const SPATIAL_GROUP_UNSCHEDULED = 'UNSCHEDULED';

/** Overview companion／filter：住宿分組（非假 dayId）。 */
export const SPATIAL_GROUP_STAYS = 'STAYS';

/** Planning／Overview 共用的未排程日標籤（繁中產品文案）。 */
export const SPATIAL_UNSCHEDULED_DAY_LABEL = '未排程';

/** Stay Pins — marker kind（ACTIVITY 既有相容；STAY 新增）。 */
export const MARKER_KIND_ACTIVITY = 'ACTIVITY';
export const MARKER_KIND_STAY = 'STAY';

export function stayMarkerKey(accommodationId) {
  if (accommodationId == null || accommodationId === '') return '';
  return `STAY:${String(accommodationId)}`;
}

/**
 * 判定 activity 座標是否可畫 marker。
 * 權威：reuse normalizeCoordinatePair（finite＋地理界線＋available!==false）。
 */
export function isSpatialMarkerEligible(location) {
  return normalizeCoordinatePair(location || {}).markerAvailable === true;
}

/**
 * 從 activity 讀取 location 標籤；不 mutate 來源。
 * 優先 nested location.name／address，其次 locationLabel。
 */
export function resolveSpatialLocationLabels(activity) {
  const location = activity?.location;
  if (location && typeof location === 'object' && location.name) {
    const name = String(location.name).trim() || 'Location';
    const address = location.address == null || String(location.address).trim() === ''
      ? ''
      : String(location.address).trim();
    return {
      locationName: name,
      locationAddress: address,
      locationLabel: name,
    };
  }
  if (activity?.locationId) {
    const label = String(activity.locationLabel || 'Location unavailable').trim()
      || 'Location unavailable';
    return {
      locationName: label,
      locationAddress: '',
      locationLabel: label,
    };
  }
  if (activity?.locationLabel) {
    const label = String(activity.locationLabel).trim() || 'Location';
    return {
      locationName: label,
      locationAddress: '',
      locationLabel: label,
    };
  }
  return {
    locationName: 'Location',
    locationAddress: '',
    locationLabel: 'Location',
  };
}

function toComparableId(value) {
  if (value == null || value === '') return '';
  return String(value);
}

function compareActivityOrder(a, b) {
  const orderA = Number(a?.displayOrder) || 0;
  const orderB = Number(b?.displayOrder) || 0;
  if (orderA !== orderB) return orderA - orderB;
  const idA = toComparableId(a?.activityId);
  const idB = toComparableId(b?.activityId);
  if (idA < idB) return -1;
  if (idA > idB) return 1;
  return 0;
}

function compareDayOrder(a, b) {
  const orderA = Number(a?.displayOrder) || 0;
  const orderB = Number(b?.displayOrder) || 0;
  if (orderA !== orderB) return orderA - orderB;
  const idA = toComparableId(a?.journeyDayId);
  const idB = toComparableId(b?.journeyDayId);
  if (idA < idB) return -1;
  if (idA > idB) return 1;
  return 0;
}

function defaultFormatDayLabel(day) {
  const order = Number(day?.displayOrder) || 0;
  const dayPart = order > 0 ? `第 ${order} 天` : '行程日';
  if (day?.date && day?.title) return `${day.date}・${dayPart}・${day.title}`;
  if (day?.date) return `${day.date}・${dayPart}`;
  if (day?.title) return `${dayPart}・${day.title}`;
  return dayPart;
}

/**
 * 將單一 activity（＋ day 脈絡）轉成 spatial marker；座標不合格回 null。
 * 不依 status 過濾；CANCELLED 只要座標有效仍納入。
 *
 * options.scheduled === true：視為已排程（來自 schedule.days[].activities），
 * 即使 journeyDayId 缺省也不落入 UNSCHEDULED（對齊 Planning 既有行為）。
 * options.forceUnscheduled === true：強制 UNSCHEDULED。
 */
export function toJourneySpatialMarker(activity, dayContext = null, options = {}) {
  if (!activity) return null;
  const coords = normalizeCoordinatePair(activity.location || {});
  if (!coords.markerAvailable) return null;

  const labels = resolveSpatialLocationLabels(activity);
  const rawDayId = activity.journeyDayId == null || activity.journeyDayId === ''
    ? (dayContext?.journeyDayId == null || dayContext.journeyDayId === ''
      ? null
      : toComparableId(dayContext.journeyDayId))
    : toComparableId(activity.journeyDayId);

  const unscheduled = options.forceUnscheduled === true
    || (options.scheduled !== true && rawDayId == null);

  const formatDayLabel = typeof options.formatDayLabel === 'function'
    ? options.formatDayLabel
    : defaultFormatDayLabel;

  const dayDisplayOrder = unscheduled
    ? Number.MAX_SAFE_INTEGER
    : (Number(dayContext?.displayOrder) || 0);
  const dayLabel = unscheduled
    ? SPATIAL_UNSCHEDULED_DAY_LABEL
    : formatDayLabel(dayContext || {});
  const dayDate = unscheduled ? null : (dayContext?.date ?? null);
  const activityId = toComparableId(activity.activityId);
  if (!activityId) return null;

  const journeyDayId = unscheduled ? null : rawDayId;
  const group = unscheduled
    ? SPATIAL_GROUP_UNSCHEDULED
    : (journeyDayId || `day-order:${dayDisplayOrder}`);

  return {
    key: activityId,
    kind: MARKER_KIND_ACTIVITY,
    activityId,
    accommodationId: null,
    journeyDayId,
    dayLabel,
    dayDate,
    dayDisplayOrder,
    activityTitle: activity.title || 'Untitled activity',
    activityDisplayOrder: Number(activity.displayOrder) || 0,
    locationName: labels.locationName,
    locationAddress: labels.locationAddress,
    locationLabel: labels.locationLabel,
    lat: coords.latitude,
    lng: coords.longitude,
    status: activity.status || 'PLANNED',
    group,
  };
}

/**
 * Stay → spatial marker；僅 nested ACTIVE location 且座標合格才產 pin。
 * 一筆 Stay 最多一個 marker（ALL mode 不重複）。
 */
export function toStaySpatialMarker(accommodation) {
  if (!accommodation) return null;
  const accommodationId = toComparableId(accommodation.accommodationId);
  if (!accommodationId) return null;
  const location = accommodation.location;
  const resolvedLocationId = toComparableId(location?.id).trim();
  if (!/^[1-9]\d*$/.test(resolvedLocationId)) return null;
  const coords = normalizeCoordinatePair(location);
  if (!coords.markerAvailable) return null;

  const name = location.name == null || String(location.name).trim() === ''
    ? (accommodation.propertyName || 'Stay')
    : String(location.name).trim();
  const address = location.address == null || String(location.address).trim() === ''
    ? ''
    : String(location.address).trim();

  return {
    key: stayMarkerKey(accommodationId),
    kind: MARKER_KIND_STAY,
    activityId: null,
    accommodationId,
    journeyDayId: null,
    dayLabel: '住宿',
    dayDate: null,
    dayDisplayOrder: Number.MAX_SAFE_INTEGER - 1,
    activityTitle: accommodation.propertyName || 'Untitled stay',
    activityDisplayOrder: 0,
    locationName: name,
    locationAddress: address,
    locationLabel: name,
    lat: coords.latitude,
    lng: coords.longitude,
    status: 'STAY',
    group: SPATIAL_GROUP_STAYS,
    checkInAt: accommodation.checkInAt || null,
    checkOutAt: accommodation.checkOutAt || null,
  };
}

/** ALL mode：每筆 Stay 最多一 pin；無 resolved coords → 略過。 */
export function buildStaySpatialMarkers(accommodations) {
  const list = Array.isArray(accommodations) ? accommodations : [];
  const markers = [];
  list.forEach((stay) => {
    const marker = toStaySpatialMarker(stay);
    if (marker) markers.push(marker);
  });
  return markers;
}

/**
 * 由 schedule 建出 deterministic spatial markers。
 * 順序：day.displayOrder → activity.displayOrder → activityId；
 * 已排程日先，Unscheduled 固定置於最後。
 * 不 mutate schedule／activity／day 物件。
 */
export function buildJourneySpatialMarkers(schedule, options = {}) {
  const markers = [];
  const days = Array.isArray(schedule?.days) ? [...schedule.days] : [];
  days.sort(compareDayOrder);

  days.forEach((day) => {
    const activities = Array.isArray(day?.activities) ? [...day.activities] : [];
    activities.sort(compareActivityOrder);
    activities.forEach((activity) => {
      const marker = toJourneySpatialMarker(activity, day, {
        ...options,
        scheduled: true,
        forceUnscheduled: false,
      });
      if (marker) markers.push(marker);
    });
  });

  const unscheduled = Array.isArray(schedule?.unscheduledActivities)
    ? [...schedule.unscheduledActivities]
    : [];
  unscheduled.sort(compareActivityOrder);
  unscheduled.forEach((activity) => {
    const marker = toJourneySpatialMarker(activity, null, {
      ...options,
      forceUnscheduled: true,
      scheduled: false,
    });
    if (marker) markers.push(marker);
  });

  return markers;
}

/**
 * Planning／Public map 既有 marker view（title／address／summary／latitude／longitude）。
 * 由 shared spatial model 適配，避免雙重 eligibility 邏輯。
 */
export function toPlanningCompatibleMapMarker(spatialMarker) {
  if (!spatialMarker) return null;
  const kind = spatialMarker.kind || MARKER_KIND_ACTIVITY;
  return {
    key: spatialMarker.key,
    kind,
    // 產品可讀類型標籤（地圖 legend／InfoWindow 用；非 API 欄位）
    kindLabel: kind === MARKER_KIND_STAY ? '住宿' : '活動',
    activityId: spatialMarker.activityId,
    accommodationId: spatialMarker.accommodationId ?? null,
    title: spatialMarker.activityTitle,
    dayLabel: spatialMarker.dayLabel,
    locationName: spatialMarker.locationName,
    address: spatialMarker.locationAddress,
    summary: spatialMarker.locationAddress
      || spatialMarker.locationName
      || spatialMarker.locationLabel
      || '',
    latitude: spatialMarker.lat,
    longitude: spatialMarker.lng,
  };
}
